import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import useFadeIn from '../hooks/useFadeIn';
import { timelineEvents, chapters } from '../data/research-data';
import './Research.css';

const chapterImages = {
  'price-of-delay': '/images/IMG_4683.jpeg',
  'returns-to-trust': '/images/nurse-holding-baby-ihs.jpeg',
  'remedy-of-the-commons': '/images/IMG_4685.jpeg',
};

const chapterImageAlts = {
  'price-of-delay': 'Black-and-white photograph of a witness testifying into a microphone at a congressional hearing during the Termination Era',
  'returns-to-trust': 'Nurse holding a baby at a Public Health Service Indian Hospital',
  'remedy-of-the-commons': 'Roadside billboard in open farmland advertising the Omaha Tribe Land Buy Back Program',
};

function throttle(fn, ms) {
  let last = 0;
  return function (...args) {
    const now = Date.now();
    if (now - last >= ms) {
      last = now;
      fn.apply(this, args);
    }
  };
}

function ChapterSection({ chapter, nextChapterId }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const ref = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('research__section--visible');
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const throttledMove = useRef(
    throttle((e, cardEl) => {
      if (!cardEl) return;
      const rect = cardEl.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      cardEl.style.transform = `perspective(600px) rotateY(${x * 8}deg) rotateX(${-y * 8}deg) scale(1.02)`;
    }, 16)
  );

  const handleMouseMove = useCallback((e) => {
    if (typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches) return;
    throttledMove.current(e, imageRef.current);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (imageRef.current) imageRef.current.style.transform = '';
  }, []);

  const copyCitation = () => {
    navigator.clipboard.writeText(chapter.cite);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const scrollToNext = () => {
    const targetId = nextChapterId || 'research-working-papers';
    const el = document.getElementById(targetId);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <article
      className="section research__section"
      id={chapter.id}
      ref={ref}
      style={{ '--chapter-color': chapter.rawColor }}
    >
      <div className="research__tint" />
      <div className="container research__chapter-layout">
        <div className="research__chapter-text">
          <p className="research__chapter-num">{chapter.number}</p>
          <p className="research__keyword" style={{ color: chapter.color }}>
            {chapter.keyword}
          </p>
          <h2 className="section-title research__title">{chapter.title}</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', marginBottom: '24px' }}>
            Elijah S. Moreno &middot; Cornell Brooks School of Public Policy
          </p>
          {expanded && (
            <div
              className="section-body research__abstract"
              style={{ borderLeftColor: chapter.color }}
            >
              {chapter.paragraphs.map((p, i) => (
                <p key={i} className="research__abstract-p" style={{ animationDelay: `${i * 120}ms` }}>{p}</p>
              ))}
            </div>
          )}
          <div className="research__btn-group" style={{ '--btn-color': chapter.rawColor }}>
            <button
              className={`research__btn ${expanded ? 'research__btn--active' : ''}`}
              onClick={() => setExpanded(!expanded)}
              aria-expanded={expanded}
              aria-label={expanded ? `Hide abstract for ${chapter.title}` : `Read abstract for ${chapter.title}`}
            >
              {expanded ? 'Hide abstract' : 'Read abstract'}
            </button>
            <span
              className="research__btn-tooltip-wrap"
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
            >
              <button
                className="research__btn research__btn--disabled"
                onClick={(e) => e.preventDefault()}
                onFocus={() => setShowTooltip(true)}
                onBlur={() => setShowTooltip(false)}
                aria-disabled="true"
                aria-label={`${chapter.downloadLabel}, coming soon`}
              >
                {chapter.downloadLabel}
              </button>
              {showTooltip && <span className="research__btn-tooltip">Coming soon</span>}
            </span>
            <button
              className="research__btn"
              onClick={copyCitation}
              aria-label={`Copy BibTeX citation for ${chapter.title}`}
            >
              {copied ? 'Copied!' : 'Cite'}
            </button>
          </div>
        </div>
        {chapterImages[chapter.id] && (
          <div
            className="research__chapter-image"
            ref={imageRef}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onTouchEnd={handleMouseLeave}
          >
            <img
              src={chapterImages[chapter.id]}
              alt={chapterImageAlts[chapter.id] || chapter.keyword}
              className="research__chapter-img"
            />
            <div className="research__chapter-img-overlay" style={{ background: chapter.rawColor }} />
            <span className="research__chapter-img-label">{chapter.keyword}</span>
          </div>
        )}
      </div>
      <button
        className="research__chapter-arrow"
        onClick={scrollToNext}
        aria-label={nextChapterId ? `Scroll to next chapter` : 'Scroll to working papers'}
      >
        <span className="timeline__scroll-arrow-icon" aria-hidden="true" />
      </button>
    </article>
  );
}

function ProgressBar({ activeChapter }) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight > 0) {
        setProgress(Math.min((window.scrollY / docHeight) * 100, 100));
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const chapter = useMemo(
    () => chapters.find(c => c.id === activeChapter),
    [activeChapter]
  );
  const barColor = chapter ? chapter.rawColor : '#1A7A8A';

  return (
    <div className="research__progress-track" role="progressbar" aria-valuenow={Math.round(progress)} aria-valuemin={0} aria-valuemax={100}>
      <div
        className="research__progress-fill"
        style={{ width: `${progress}%`, background: barColor }}
      />
    </div>
  );
}

function Breadcrumb({ activeChapter }) {
  const chapter = useMemo(
    () => chapters.find(c => c.id === activeChapter),
    [activeChapter]
  );
  return (
    <nav className="research__breadcrumb" aria-label="Breadcrumb">
      <div className="container--narrow">
        <Link to="/" className="research__breadcrumb-link">Home</Link>
        <span className="research__breadcrumb-sep" aria-hidden="true">/</span>
        <span className="research__breadcrumb-current">Research</span>
        {chapter && (
          <>
            <span className="research__breadcrumb-sep" aria-hidden="true">/</span>
            <span className="research__breadcrumb-chapter" style={{ color: chapter.rawColor }}>
              {chapter.number}: {chapter.keyword}
            </span>
          </>
        )}
      </div>
      <ProgressBar activeChapter={activeChapter} />
    </nav>
  );
}

const categories = [
  { key: 'destruction', label: 'Destruction', color: 'var(--color-destruction)', cssVar: '--color-destruction' },
  { key: 'trust', label: 'Trust', color: 'var(--color-trust)', cssVar: '--color-trust' },
  { key: 'partnership', label: 'Partnership', color: 'var(--color-partnership)', cssVar: '--color-partnership' },
  { key: 'restoration', label: 'Restoration', color: 'var(--color-restoration)', cssVar: '--color-restoration' },
  { key: 'pending', label: 'Pending', color: 'var(--color-pending)', cssVar: '--color-pending' },
];

const categoryByColor = {
  'var(--color-destruction)': 'destruction',
  'var(--color-trust)': 'trust',
  'var(--color-partnership)': 'partnership',
  'var(--color-restoration)': 'restoration',
  'var(--color-teaching)': 'partnership',
  'var(--color-pending)': 'pending',
};

function Timeline() {
  const navigate = useNavigate();
  const timelineRef = useRef(null);
  useFadeIn(timelineRef);
  const [pendingTooltip, setPendingTooltip] = useState(null);
  const [activeCategories, setActiveCategories] = useState(() => new Set(categories.map(c => c.key)));

  const toggleCategory = (key) => {
    setActiveCategories(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  useEffect(() => {
    if (pendingTooltip == null) return;
    const timer = setTimeout(() => setPendingTooltip(null), 1800);
    return () => clearTimeout(timer);
  }, [pendingTooltip]);

  const handleClick = (evt, i) => {
    if (evt.pending) {
      setPendingTooltip(i);
      return;
    }
    if (evt.href) {
      window.open(evt.href, '_blank', 'noopener');
      return;
    }
    if (!evt.link) return;
    if (evt.link.startsWith('/')) {
      navigate(evt.link);
    } else {
      const el = document.getElementById(evt.link);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleKeyDown = (e, evt, i) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick(evt, i);
    }
  };

  return (
    <section className="section section--first research__timeline-section" ref={timelineRef}>
      <div className="container">
        <div className="research__hero">
          <h1 className="section-title research__hero-title fade-in">The Governance Production Function</h1>
          <div className="section-body research__hero-body fade-in fade-in--delay-1">
            <p>
              My research asks when governance is a binding constraint,
              and through what margins it operates. Across settings such as health,
              land, demography, gender, and public policy, I find that governance is rarely
              a background condition and often determines whether policy works at all.
            </p>
          </div>
        </div>
        <h2 className="section-title research__timeline-title fade-in fade-in--delay-2">Research Agenda Through Policy</h2>
        <p className="timeline__scroll-hint fade-in fade-in--delay-2">Scroll to explore the timeline</p>
        <div className="timeline fade-in fade-in--delay-3" role="list">
          <div className="timeline__line" />
          {timelineEvents.map((evt, i) => {
            const cat = categoryByColor[evt.color] || 'pending';
            const isFiltered = !activeCategories.has(cat);
            return (
            <div
              key={i}
              className={`timeline__event ${evt.link || evt.href || evt.pending ? 'timeline__event--clickable' : ''} ${evt.greyed ? 'timeline__event--greyed' : ''} ${evt.pending ? 'timeline__event--pending' : ''} ${isFiltered ? 'timeline__event--filtered' : ''}`}
              style={{ '--evt-color': evt.color || 'var(--text-muted)' }}
              onClick={() => !isFiltered && handleClick(evt, i)}
              onKeyDown={(e) => !isFiltered && handleKeyDown(e, evt, i)}
              role="listitem"
              tabIndex={evt.link || evt.href || evt.pending ? 0 : undefined}
            >
              <span className="timeline__year">{evt.year}</span>
              <span className="timeline__tick" />
              <span className="timeline__dot" />
              <span className="timeline__label">{evt.label}</span>
              <span className="timeline__desc">{evt.desc}</span>
              {evt.pending && pendingTooltip === i && (
                <span className="timeline__tooltip">Coming soon</span>
              )}
            </div>
            );
          })}
        </div>
        <p className="timeline__click-hint">Select a highlighted event to explore my research</p>
        <p className="timeline__legend-label">Policy Categories</p>
        <div className="timeline__legend">
          {categories.map(cat => (
            <button
              key={cat.key}
              className={`timeline__legend-item ${activeCategories.has(cat.key) ? '' : 'timeline__legend-item--off'}`}
              onClick={() => toggleCategory(cat.key)}
              aria-pressed={activeCategories.has(cat.key)}
            >
              <span className="timeline__legend-swatch" style={{ background: cat.color }} />
              {cat.label}
            </button>
          ))}
        </div>
        <button
          className="timeline__scroll-arrow"
          onClick={() => {
            const el = document.getElementById('price-of-delay');
            if (el) el.scrollIntoView({ behavior: 'smooth' });
          }}
          aria-label="Scroll to Chapter 1"
        >
          <span className="timeline__scroll-arrow-icon" aria-hidden="true" />
        </button>
      </div>
    </section>
  );
}

export default function Research() {
  const [activeChapter, setActiveChapter] = useState(null);
  const [showNav, setShowNav] = useState(false);
  const mainRef = useRef(null);
  useFadeIn(mainRef);

  useEffect(() => {
    const onScroll = () => {
      setShowNav(window.scrollY > 200);

      const offset = window.innerHeight * 0.35;
      let current = null;
      for (const ch of chapters) {
        const el = document.getElementById(ch.id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= offset) {
            current = ch.id;
          }
        }
      }
      setActiveChapter(current);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <main id="main-content" ref={mainRef}>
      <SEO
        title="Research"
        description="Research on federal Indian policy, tribal governance, institutional destruction, public health trust, and land consolidation by Elijah S. Moreno."
        path="/research"
        image="https://elijahmoreno.com/images/IMG_4683.jpeg"
        imageWidth={1000}
        imageHeight={750}
        ogType="article"
        articleMeta={{ author: 'Elijah S. Moreno', publishedTime: '2025-01-01' }}
      />
      {showNav && <Breadcrumb activeChapter={activeChapter} />}

      <Timeline />

      {chapters.map((chapter, i) => (
        <ChapterSection
          key={chapter.id}
          chapter={chapter}
          nextChapterId={i < chapters.length - 1 ? chapters[i + 1].id : null}
        />
      ))}

      <section className="section research__working-papers" id="research-working-papers">
        <div className="container--narrow" style={{ textAlign: 'center' }}>
          <p className="research__wp-label fade-in">In Progress</p>
          <h2 className="section-title fade-in fade-in--delay-1">Working Papers</h2>
          <p className="research__wp-desc fade-in fade-in--delay-2">
            More research is currently underway, with details to come.
          </p>
          <div className="research__wp-topics fade-in fade-in--delay-3">
            {['Climate Change', 'Institutional Development', 'Political Influence', 'Gender Identification', 'Fertility'].map((topic) => (
              <span key={topic} className="research__wp-pill">{topic}</span>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
